import {
  type ChangeEvent,
  type FC,
  useCallback,
  useMemo,
  useState,
} from 'react'

import { HexAlphaColorPicker } from 'react-colorful'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import type { DialogProps } from '@/dialogs/types'

interface Props extends DialogProps {
  onColorChange: (newColor: string) => void
  oldColor: string | undefined
}

export const ColorPickerDialog: FC<Props> = ({
  onClose,
  onColorChange,
  isOpen,
  oldColor,
}) => {
  const [newColor, setNewColor] = useState(oldColor ?? '')

  const handleNewColor = useCallback((color: string) => {
    const value = color.replace(new RegExp('NaN', 'g'), 'ff')

    setNewColor(value)
  }, [])

  const resetButton = useMemo(
    () => (
      <span
        className="select-none cursor-pointer truncate max-w-auto w-auto text-xs text-primary-500"
        onClick={() => setNewColor('')}
      >
        Reset Color
      </span>
    ),
    [],
  )

  const handleSubmit = useCallback(() => {
    onClose()
    onColorChange(newColor)
  }, [newColor, onClose, onColorChange])

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      handleNewColor(event.target.value),
    [handleNewColor],
  )

  return (
    <Dialog show={isOpen}>
      <HexAlphaColorPicker
        color={oldColor}
        onChange={handleNewColor}
      />

      <div className="mt-6 w-full">
        <TextField
          value={newColor}
          suffix={resetButton}
          onChange={handleInputChange}
        />
      </div>

      <Dialog.Footer className="mt-6">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          className="flex-1"
          onClick={handleSubmit}
        >
          Save
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
